import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { AuthContext } from '../App';

export default function AdminPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  // User lookup
  const [lookupUser, setLookupUser] = useState('');
  const [userHistory, setUserHistory] = useState(null);

  // Payouts
  const [payouts, setPayouts] = useState([]);
  const [payoutFilter, setPayoutFilter] = useState('pending');
  const [payoutLoading, setPayoutLoading] = useState(false);

  // DMCA
  const [dmcaClaims, setDmcaClaims] = useState([]);
  const [dmcaFilter, setDmcaFilter] = useState('pending');
  const [dmcaLoading, setDmcaLoading] = useState(false);

  // Action modals
  const [actionTarget, setActionTarget] = useState(null); // { username, action }
  const [actionReason, setActionReason] = useState('');
  const [actionDuration, setActionDuration] = useState(24);
  const [actionPermanent, setActionPermanent] = useState(false);
  const [actionStatus, setActionStatus] = useState('');

  useEffect(() => {
    if (!user?.is_admin) return;
    setLoading(true);
    api.adminReports(filter).then(setReports).catch(() => {}).finally(() => setLoading(false));
  }, [user, filter]);

  if (!user?.is_admin) {
    return (
      <div className="dashboard">
        <h1>Admin</h1>
        <div className="offline-banner">
          <h2>Access denied</h2>
          <p>You need admin privileges to view this page.</p>
        </div>
      </div>
    );
  }

  const handleResolve = async (id, status) => {
    try {
      await api.adminResolve(id, { status, admin_notes: '' });
      setReports(prev => prev.filter(r => r.id !== id));
    } catch (e) { alert(e.message); }
  };

  const handleAction = async () => {
    if (!actionTarget) return;
    setActionStatus('');
    try {
      if (actionTarget.action === 'warn') {
        await api.adminWarn(actionTarget.username, { reason: actionReason });
        setActionStatus('Warning sent');
      } else if (actionTarget.action === 'suspend') {
        await api.adminSuspend(actionTarget.username, {
          reason: actionReason,
          duration_hours: actionDuration,
          permanent: actionPermanent,
        });
        setActionStatus('Account suspended');
      } else if (actionTarget.action === 'unsuspend') {
        await api.adminUnsuspend(actionTarget.username);
        setActionStatus('Suspension lifted');
      }
      setTimeout(() => { setActionTarget(null); setActionStatus(''); setActionReason(''); }, 1500);
    } catch (e) {
      setActionStatus(`Error: ${e.message}`);
    }
  };

  const handleLookup = async () => {
    if (!lookupUser.trim()) return;
    try {
      const res = await api.adminUserHistory(lookupUser.trim().toLowerCase());
      setUserHistory(res);
    } catch (e) {
      setUserHistory(null);
      alert(e.message);
    }
  };

  return (
    <div className="dashboard">
      <h1>Admin Panel</h1>

      {/* User Lookup */}
      <div className="stream-setup">
        <h2>User Lookup</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input className="input" placeholder="Username" value={lookupUser} onChange={e => setLookupUser(e.target.value)}
            style={{ marginBottom: 0, flex: 1 }} onKeyDown={e => e.key === 'Enter' && handleLookup()} />
          <button className="btn btn-primary" onClick={handleLookup}>Look Up</button>
        </div>

        {userHistory && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ padding: '14px', background: '#0e0e10', borderRadius: '4px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <span style={{ fontWeight: 800, fontSize: '1rem' }}>{userHistory.user.display_name || userHistory.user.username}</span>
                  <span style={{ color: '#adadb8', marginLeft: '8px' }}>@{userHistory.user.username}</span>
                  {userHistory.user.is_suspended && <span style={{ marginLeft: '8px', color: '#eb0400', fontWeight: 700, fontSize: '0.8rem' }}>SUSPENDED</span>}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                    onClick={() => { setActionTarget({ username: userHistory.user.username, action: 'warn' }); setActionReason(''); }}>
                    Warn
                  </button>
                  {userHistory.user.is_suspended ? (
                    <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                      onClick={() => { setActionTarget({ username: userHistory.user.username, action: 'unsuspend' }); }}>
                      Unsuspend
                    </button>
                  ) : (
                    <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                      onClick={() => { setActionTarget({ username: userHistory.user.username, action: 'suspend' }); setActionReason(''); }}>
                      Suspend
                    </button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: '0.82rem', color: '#adadb8', marginTop: '8px' }}>
                Email: {userHistory.user.email} | Warnings: {userHistory.user.warning_count || 0} | Joined: {new Date(userHistory.user.created_at).toLocaleDateString()}
              </div>
            </div>

            {userHistory.actions.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 700, marginBottom: '8px', textTransform: 'uppercase' }}>Mod Actions</div>
                {userHistory.actions.map(a => (
                  <div key={a.id} style={{ padding: '8px 12px', background: '#0e0e10', borderRadius: '4px', marginBottom: '4px', fontSize: '0.82rem' }}>
                    <span style={{ fontWeight: 700, color: a.action === 'suspend' ? '#eb0400' : a.action === 'warn' ? '#ffa726' : '#00c400' }}>
                      {a.action.toUpperCase()}
                    </span>
                    <span style={{ color: '#adadb8', marginLeft: '8px' }}>by @{a.admin_username}</span>
                    {a.reason && <span style={{ color: '#adadb8', marginLeft: '8px' }}>— {a.reason}</span>}
                    <span style={{ color: '#666', marginLeft: '8px', fontSize: '0.75rem' }}>{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}

            {userHistory.reports.length > 0 && (
              <div>
                <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 700, marginBottom: '8px', textTransform: 'uppercase' }}>Reports Against</div>
                {userHistory.reports.map(r => (
                  <div key={r.id} style={{ padding: '8px 12px', background: '#0e0e10', borderRadius: '4px', marginBottom: '4px', fontSize: '0.82rem' }}>
                    <span style={{ fontWeight: 700 }}>{r.reason}</span>
                    <span style={{ color: '#adadb8', marginLeft: '8px' }}>by @{r.reporter_username}</span>
                    <span style={{
                      marginLeft: '8px', fontSize: '0.72rem', fontWeight: 700,
                      color: r.status === 'pending' ? '#ffa726' : r.status === 'resolved' ? '#00c400' : '#adadb8'
                    }}>{r.status.toUpperCase()}</span>
                    {r.details && <div style={{ color: '#adadb8', marginTop: '4px' }}>{r.details}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reports */}
      <div className="stream-setup">
        <h2>Reports</h2>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
          {['pending', 'resolved', 'dismissed'].map(s => (
            <button key={s} className={`btn ${filter === s ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '6px 14px', fontSize: '0.82rem', textTransform: 'capitalize' }}
              onClick={() => setFilter(s)}>
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ color: '#adadb8', padding: '20px', textAlign: 'center' }}>Loading...</div>
        ) : reports.length === 0 ? (
          <div style={{ color: '#adadb8', padding: '20px', textAlign: 'center' }}>No {filter} reports</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {reports.map(r => (
              <div key={r.id} style={{ padding: '14px', background: '#0e0e10', borderRadius: '4px', border: '1px solid #2a2a2e' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>
                      {r.reason}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#adadb8' }}>
                      <span style={{ color: '#eb0400', cursor: 'pointer' }} onClick={() => { setLookupUser(r.reported_username); handleLookup(); }}>
                        @{r.reported_username}
                      </span>
                      <span style={{ margin: '0 6px' }}>reported by</span>
                      <span>@{r.reporter_username}</span>
                      <span style={{ margin: '0 6px' }}>|</span>
                      <span>{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                    {r.details && <div style={{ fontSize: '0.82rem', color: '#adadb8', marginTop: '6px', fontStyle: 'italic' }}>"{r.details}"</div>}
                    {r.target_warnings > 0 && <div style={{ fontSize: '0.75rem', color: '#ffa726', marginTop: '4px' }}>{r.target_warnings} previous warning(s)</div>}
                  </div>
                  {r.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                        onClick={() => { setActionTarget({ username: r.reported_username, action: 'warn' }); setActionReason(r.reason); }}>
                        Warn
                      </button>
                      <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                        onClick={() => { setActionTarget({ username: r.reported_username, action: 'suspend' }); setActionReason(r.reason); }}>
                        Suspend
                      </button>
                      <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                        onClick={() => handleResolve(r.id, 'resolved')}>
                        Resolve
                      </button>
                      <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                        onClick={() => handleResolve(r.id, 'dismissed')}>
                        Dismiss
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DMCA Claims */}
      <div className="stream-setup">
        <h2>DMCA Claims</h2>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
          {['pending', 'resolved', 'dismissed'].map(s => (
            <button key={s} className={`btn ${dmcaFilter === s ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '6px 14px', fontSize: '0.82rem', textTransform: 'capitalize' }}
              onClick={() => {
                setDmcaFilter(s);
                setDmcaLoading(true);
                api.adminDmca(s).then(setDmcaClaims).catch(() => {}).finally(() => setDmcaLoading(false));
              }}>{s}</button>
          ))}
        </div>

        {!dmcaLoading && dmcaClaims.length === 0 && dmcaFilter === 'pending' && (
          <button className="btn btn-outline" style={{ fontSize: '0.82rem' }} onClick={() => {
            setDmcaLoading(true);
            api.adminDmca('pending').then(setDmcaClaims).catch(() => {}).finally(() => setDmcaLoading(false));
          }}>Load Claims</button>
        )}

        {dmcaLoading ? (
          <div style={{ color: '#adadb8', padding: '20px', textAlign: 'center' }}>Loading...</div>
        ) : dmcaClaims.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {dmcaClaims.map(c => (
              <div key={c.id} style={{ padding: '14px', background: '#0e0e10', borderRadius: '4px', border: '1px solid #2a2a2e' }}>
                <div style={{ fontWeight: 700, marginBottom: '4px' }}>From: {c.claimant_name} ({c.claimant_email})</div>
                <div style={{ fontSize: '0.82rem', color: '#adadb8', marginBottom: '4px' }}>{c.description}</div>
                {c.content_url && <div style={{ fontSize: '0.78rem', color: '#adadb8' }}>Content: <a href={c.content_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{c.content_url}</a></div>}
                {c.original_work && <div style={{ fontSize: '0.78rem', color: '#adadb8' }}>Original: <a href={c.original_work} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{c.original_work}</a></div>}
                <div style={{ fontSize: '0.72rem', color: '#666', marginTop: '4px' }}>{new Date(c.created_at).toLocaleString()}</div>
                {c.status === 'pending' && (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                    <button className="btn btn-danger" style={{ padding: '4px 12px', fontSize: '0.78rem' }} onClick={async () => {
                      const username = prompt('Issue copyright strike to username:');
                      if (username) {
                        await api.adminAddStrike(username, { reason: c.description, dmca_claim_id: c.id });
                        await api.adminResolveDmca(c.id, { status: 'resolved', admin_notes: `Strike issued to @${username}` });
                        setDmcaClaims(prev => prev.filter(x => x.id !== c.id));
                        alert(`Copyright strike issued to @${username}`);
                      }
                    }}>Strike User</button>
                    <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: '0.78rem' }} onClick={async () => {
                      await api.adminResolveDmca(c.id, { status: 'resolved', admin_notes: 'Resolved without strike' });
                      setDmcaClaims(prev => prev.filter(x => x.id !== c.id));
                    }}>Resolve</button>
                    <button className="btn btn-outline" style={{ padding: '4px 12px', fontSize: '0.78rem' }} onClick={async () => {
                      await api.adminResolveDmca(c.id, { status: 'dismissed', admin_notes: 'Claim dismissed' });
                      setDmcaClaims(prev => prev.filter(x => x.id !== c.id));
                    }}>Dismiss</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : dmcaFilter !== 'pending' ? (
          <div style={{ color: '#adadb8', padding: '20px', textAlign: 'center' }}>No {dmcaFilter} claims</div>
        ) : null}
      </div>

      {/* Payouts */}
      <div className="stream-setup">
        <h2>💰 Payouts</h2>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {['pending', 'completed', 'rejected'].map(s => (
            <button key={s} className={`btn ${payoutFilter === s ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '6px 14px', fontSize: '0.82rem', textTransform: 'capitalize' }}
              onClick={() => {
                setPayoutFilter(s);
                setPayoutLoading(true);
                api.adminPayouts(s).then(setPayouts).catch(() => {}).finally(() => setPayoutLoading(false));
              }}>
              {s}
            </button>
          ))}
          <button className="btn btn-outline" style={{ padding: '6px 14px', fontSize: '0.82rem', marginLeft: 'auto' }}
            onClick={async () => {
              try {
                const res = await api.adminGeneratePayouts();
                alert(res.message);
                setPayoutLoading(true);
                api.adminPayouts('pending').then(setPayouts).catch(() => {}).finally(() => setPayoutLoading(false));
                setPayoutFilter('pending');
              } catch (e) { alert(e.message); }
            }}>
            Generate Weekly Payouts
          </button>
        </div>

        {!payoutLoading && payouts.length === 0 && payoutFilter === 'pending' && (
          <button className="btn btn-outline" style={{ fontSize: '0.82rem' }} onClick={() => {
            setPayoutLoading(true);
            api.adminPayouts('pending').then(setPayouts).catch(() => {}).finally(() => setPayoutLoading(false));
          }}>Load Payouts</button>
        )}

        {payoutLoading ? (
          <div style={{ color: '#adadb8', padding: '20px', textAlign: 'center' }}>Loading...</div>
        ) : payouts.length === 0 ? (
          <div style={{ color: '#adadb8', padding: '20px', textAlign: 'center' }}>No {payoutFilter} payouts</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {payouts.map(p => (
              <div key={p.id} style={{ padding: '14px', background: '#0e0e10', borderRadius: '4px', border: '1px solid #2a2a2e' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--accent)' }}>
                      ${(p.amount_cents / 100).toFixed(2)}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#adadb8', marginTop: '4px' }}>
                      <strong style={{ color: '#efeff1' }}>@{p.username}</strong> ({p.display_name})
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#adadb8', marginTop: '2px' }}>
                      PayPal: <strong>{p.payout_email}</strong>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#666', marginTop: '4px' }}>
                      Requested: {new Date(p.created_at).toLocaleString()}
                      {p.processed_at && ` | Processed: ${new Date(p.processed_at).toLocaleString()}`}
                    </div>
                    {p.admin_notes && <div style={{ fontSize: '0.78rem', color: '#adadb8', marginTop: '4px', fontStyle: 'italic' }}>Note: {p.admin_notes}</div>}
                  </div>
                  {p.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '0.82rem' }}
                        onClick={async () => {
                          const notes = prompt('Notes (optional):') || '';
                          await api.adminProcessPayout(p.id, { status: 'completed', admin_notes: notes });
                          setPayouts(prev => prev.filter(x => x.id !== p.id));
                        }}>
                        Mark Paid
                      </button>
                      <button className="btn btn-danger" style={{ padding: '6px 14px', fontSize: '0.82rem' }}
                        onClick={async () => {
                          const notes = prompt('Reason for rejection:') || '';
                          await api.adminProcessPayout(p.id, { status: 'rejected', admin_notes: notes });
                          setPayouts(prev => prev.filter(x => x.id !== p.id));
                        }}>
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Modal */}
      {actionTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#18181b', borderRadius: '2px', padding: '28px', width: '100%', maxWidth: '440px', boxShadow: '4px 4px 0 rgba(0,0,0,0.5)', border: '1px solid #2a2a2e' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 800, textTransform: 'capitalize' }}>
                {actionTarget.action} @{actionTarget.username}
              </h2>
              <button onClick={() => { setActionTarget(null); setActionStatus(''); }}
                style={{ background: 'none', border: 'none', color: '#adadb8', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
            </div>

            {actionTarget.action !== 'unsuspend' && (
              <>
                <div className="form-group">
                  <label>Reason</label>
                  <textarea className="input" value={actionReason} onChange={e => setActionReason(e.target.value)}
                    placeholder="Reason for this action..." rows={2} style={{ resize: 'vertical', fontFamily: 'inherit', marginBottom: 0 }} />
                </div>

                {actionTarget.action === 'suspend' && (
                  <div className="form-group" style={{ marginTop: '12px' }}>
                    <label>Duration</label>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {[24, 72, 168, 720].map(h => (
                        <button key={h} className={`btn ${actionDuration === h && !actionPermanent ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                          onClick={() => { setActionDuration(h); setActionPermanent(false); }}>
                          {h < 48 ? `${h}h` : `${Math.floor(h / 24)}d`}
                        </button>
                      ))}
                      <button className={`btn ${actionPermanent ? 'btn-danger' : 'btn-secondary'}`}
                        style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                        onClick={() => setActionPermanent(true)}>
                        Permanent
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {actionStatus && <p style={{ fontSize: '0.85rem', marginBottom: '12px', color: actionStatus.startsWith('Error') ? '#eb0400' : '#00c400' }}>{actionStatus}</p>}

            <button
              className={`btn ${actionTarget.action === 'suspend' ? 'btn-danger' : actionTarget.action === 'unsuspend' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ width: '100%' }}
              onClick={handleAction}>
              {actionTarget.action === 'warn' ? 'Send Warning' : actionTarget.action === 'suspend' ? 'Suspend Account' : 'Lift Suspension'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
